import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useChats } from '@/data/store';
import type { TeamChat } from '@/data/types';
import { TeamAvatar } from './Avatar';
import { Switch } from './Switch';
import { useOverlay } from './Overlay';
import { tasksChanged } from '@/lib/tedTasks';

// Assistants in a group's Info, head office only (Gary, 1 Nov 2026): Ted coaches the members ticked here, never head
// office, and posts only once switched on. The group then lists under Coach. Anyone else added to the group later
// silences him there until they leave or are ticked (the server's outsider rule).

interface Row { scope_user_ids: string[]; active: boolean }

export function GroupCoach({ chat }: { chat: TeamChat }) {
  const { setGroupCoach } = useChats();
  const { toast } = useOverlay();
  const [row, setRow] = useState<Row | null>(null);
  const [operators, setOperators] = useState<{ id: string; name: string }[] | null>(null);
  const [busy, setBusy] = useState(false);
  const memberKey = (chat.members ?? []).map((m) => m.id).join(',');

  useEffect(() => {
    let gone = false;
    void (async () => {
      const ids = (chat.members ?? []).map((m) => m.id);
      const [coach, people] = await Promise.all([
        supabase.from('ted_coaching').select('scope_user_ids, active').eq('thread_id', chat.id).maybeSingle(),
        ids.length ? supabase.from('users').select('id, role').in('id', ids) : Promise.resolve({ data: [] }),
      ]);
      if (gone) return;
      setRow((coach.data as Row | null) ?? { scope_user_ids: [], active: false });
      const headOffice = new Set(((people.data ?? []) as { id: string; role: string | null }[]).filter((p) => p.role === 'super_admin').map((p) => p.id));
      setOperators((chat.members ?? []).filter((m) => !headOffice.has(m.id) && m.name !== 'You').map((m) => ({ id: m.id, name: m.name })));
    })();
    return () => { gone = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when the members change, not on every message
  }, [chat.id, memberKey]);

  if (!row || !operators) return null;

  async function save(scope: string[], on: boolean) {
    setBusy(true);
    const err = await setGroupCoach(chat.id, scope, on);
    setBusy(false);
    if (err) { toast(err); return; }
    setRow({ scope_user_ids: scope, active: on });
    tasksChanged();
    toast(on ? 'Ted is coaching this group' : 'Ted is off in this group');
  }

  const picked = row.scope_user_ids.filter((id) => operators.some((o) => o.id === id));
  return (
    <>
      <p className="chats-group-title">Assistants · head office only</p>
      <div className="chats-group">
        <button type="button" className="chats-set" disabled={busy || (!row.active && !picked.length)}
          onClick={() => void save(picked, !row.active)}>
          <TeamAvatar who="ted" small />
          <div className="chats-set-main">
            Ted, sales coach
            <small>{row.active ? 'On: plans the week, daily actions and the task list here.' : picked.length ? 'Off. Switch on to start coaching.' : 'Tick who he coaches first.'}</small>
          </div>
          <Switch on={row.active} label="Ted coaches this group" />
        </button>
        {operators.length === 0 && <div className="chats-set"><div className="chats-set-main"><small>Add the area's operators to the group first.</small></div></div>}
        {operators.map((o) => {
          const on = picked.includes(o.id);
          return (
            <button key={o.id} type="button" className="chats-set" disabled={busy || (on && row.active && picked.length === 1)}
              onClick={() => void save(on ? picked.filter((id) => id !== o.id) : [...picked, o.id], row.active)}>
              <span className={`chats-task-tick${on ? ' chats-task-tick--done' : ''}`} aria-hidden="true">{on ? '✓' : ''}</span>
              <div className="chats-set-main">{o.name}<small>{on ? 'Ted coaches them' : 'Not coached'}</small></div>
            </button>
          );
        })}
      </div>
    </>
  );
}
